import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { useApp, useBehavior, useSubscribeOnWsUpdate, useUpdateWidget, currencyFormat, eventBus, significantDecimalPlaces } from '@libs/core';
import { ActionActionTypeEnum, AssetsSvgMap, TWidget, TWidgetProps } from '@libs/types';
import React, { useEffect, useMemo } from 'react';
import { TBalance, TBalanceData } from '../../types/data';
import { EConsumerLocalStorageItems } from '../../types/enums';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import SelectBalanceBottomSheet from './SelectBalanceBottomSheet';
import { getIconForCurrencyIso } from './utils/balanceUtils';

const getCurrentBalance = (balances: TBalance[], value: string): TBalance | null => {
    if (Array.isArray(balances) && balances?.length > 0) {
        const savedIso = localStorage.getItem(EConsumerLocalStorageItems.PREFERRED_BALANCE);
        const savedBalance = balances.find((b) => b?.iso === savedIso);
        return savedBalance ?? balances.find((b) => b?.id === value) ?? balances[0];
    }
    return null;
};

export const Balance: React.FC<TWidgetProps<TBalanceData>> = ({ widget }) => {
    const data = widget.data ?? {};

    const { setPopup } = useApp();
    const { updateWidget, updateWidgetDataByKey } = useUpdateWidget(widget.uid);
    const { isHidden } = useBehavior(widget);

    const currentBalance = useMemo(() => {
        const currentBalance = getCurrentBalance(data.balances, data.value as string);
        return currentBalance
            ? {
                  currentBalance: currentBalance,
                  showArrow: data.balances.length > 1 || data.canGetPaysendCard || data.canExchange || data.canAddCurrency,
              }
            : null;
    }, [data.balances, data.canAddCurrency, data.canExchange, data.canGetPaysendCard, data.value]);

    const cb = currentBalance?.currentBalance;
    const hasCurrencyIcon = !!getIconForCurrencyIso(cb?.iso);

    const arrActions = useMemo(() => widget.actions?.find((a) => a?.actionType === ActionActionTypeEnum.onTimer), [widget.actions]);

    useSubscribeOnWsUpdate(arrActions);

    useEffect(() => {
        if (widget.internalId) {
            eventBus.subscribeWS(widget.internalId, (w) => {
                if (w) {
                    const newBalanceWidget = w as unknown as TWidget<TBalanceData>;
                    updateWidget({ ...newBalanceWidget, data: { ...newBalanceWidget.data, value: cb?.id } });
                }
            });
        }
        if (cb?.id && data.value !== cb.id) {
            updateWidgetDataByKey('value', cb.id);
        }
        return () => {
            eventBus.unsubscribeWsSpecificWidget(widget.internalId);
        };
    }, [cb?.id, data.value, updateWidget, updateWidgetDataByKey, widget.internalId]);

    if (!cb) {
        return false;
    }

    const decimalLength = significantDecimalPlaces(cb.balance) > 0 ? 2 : 0;

    const amountText = hasCurrencyIcon ? (currencyFormat(cb.balance, cb.iso, decimalLength) as string) : `${cb.balance} ${cb.iso?.toLowerCase()}`;

    const amountTextTransformation = hasCurrencyIcon
        ? undefined
        : [
              {
                  text: `${cb.balance}`,
                  precision: decimalLength,
                  useLocaleNumberFormat: true,
              },
              {
                  text: cb.iso?.toLowerCase(),
                  smallCaps: true,
              },
          ];
    const parentStyleNames = [widget.options?.style].filter(Boolean) as string[];

    return (
        <CoreLayoutView
            widget={widget}
            uid={widget.uid}
            key={cb.id}
            isHide={isHidden}
            styleName={widget.options?.style}
            styleProps={{
                orientation: ORIENTATION_ENUM.VERTICAL,
                lineX: 'center',
                gap: 8,
                padding: { top: 4, right: 8, bottom: 8, left: 8 },
                borderRadius: 12,
                margin: widget.options?.margin,
                width: 'auto',
                hovered: { backgroundColor: 'var(--color-white)' },
                pressed: { backgroundColor: 'var(--color-white_40_tr_base)' },
            }}
            style={{ alignSelf: 'center' }}
            onClick={
                currentBalance.showArrow
                    ? () => {
                          setPopup(
                              <SelectBalanceBottomSheet
                                  parentStyleNames={parentStyleNames}
                                  value={cb.id}
                                  widget={widget}
                                  storageKey={EConsumerLocalStorageItems.PREFERRED_BALANCE}
                              />,
                          );
                      }
                    : undefined
            }
        >
            <CoreTextView
                uid={widget.uid + '_amount'}
                text={amountText}
                styleName="balanceWidgetAmount"
                parentStyleNames={parentStyleNames}
                textTransformation={amountTextTransformation}
                styleProps={{
                    ...TextStyle.display1,
                    main: 'var(--color-black)',
                    maxLines: 1,
                }}
            />
            <CoreLayoutView
                uid={widget.uid + '_currency'}
                styleName="balanceWidgetCurrencyRow"
                parentStyleNames={parentStyleNames}
                styleProps={{
                    orientation: ORIENTATION_ENUM.HORIZONTAL,
                    lineX: 'center',
                    gap: 2,
                }}
            >
                <CoreTextView
                    text={cb.name}
                    styleName="balanceWidgetCurrencyName"
                    parentStyleNames={parentStyleNames}
                    styleProps={{
                        ...TextStyle.secondary,
                        main: 'var(--color-grey_72_lc)',
                    }}
                />
                {currentBalance.showArrow && (
                    <VisualView
                        iconName={AssetsSvgMap.ic_chevron_down_small}
                        size="xxSmall"
                        parentStyleNames={[...parentStyleNames, 'balanceWidgetIcon']}
                        iconColor="var(--color-grey_72_lc)"
                    />
                )}
            </CoreLayoutView>
        </CoreLayoutView>
    );
};
